import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  TextField,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  Paper,
  CircularProgress,
  Stack
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { t } from "i18next";
import { setMobileBackColor, setMobileCardBorderColor, setMobileTextColor } from "../../utility";
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from "react-leaflet";

// Configure Leaflet icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

// Reverse geocode helper function - Using OpenStreetMap Nominatim (free)
const reverseGeocode = async (lat, lng) => {
  const url = `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${lat}&lon=${lng}`;
  const res = await fetch(url);
  const data = await res.json();
  return data;
};

const forwardGeocode = async (query, limit = 20) => {
  const url = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=${limit}`;
  const res = await fetch(url);
  return await res.json();
};

// Component to control map view
function MapController({ center, zoom }) {
  const map = useMap();

  // useEffect(() => {
  //   if (center) {
  //     map.flyTo(center, zoom);
  //   }
  // }, [center, zoom, map]);

  useEffect(() => {
    if (center) {
      map.flyTo(center, map.getZoom()); // ✅ use current zoom
    }
  }, [center]);

  return null;
}

function LocationSelector({ setLatLng, updateLocationFormData, setLocationDetails }) {
  const map = useMap();

  useMapEvents({
    async click(e) {
      const { lat, lng } = e.latlng;
      setLatLng({ lat, lng });
      try {
        const data = await reverseGeocode(lat, lng);
        updateLocationFormData(data.address);
        setLocationDetails({
          displayName: data.display_name,
          address: data.address,
          coordinates: { lat, lng }
        });
      } catch (error) {
        console.error("Reverse geocoding failed:", error);
      }
    },
  });



  return null;
}

function LocateControl({ setLatLng, updateLocationFormData, setLocationDetails }) {
  const map = useMap();

  const handleLocateClick = () => {
    if (!navigator.geolocation) {
      alert("Geolocation is not supported by your browser.");
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const coords = {
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
        };
        setLatLng(coords);
        map.flyTo(coords, 16);

        try {
          const data = await reverseGeocode(coords.lat, coords.lng);
          updateLocationFormData(data.address);
          setLocationDetails({
            displayName: data.display_name,
            address: data.address,
            coordinates: coords
          });
        } catch (error) {
          console.warn("Geocode error:", error);
        }
      },
      (err) => {
        console.warn("Geolocation error:", err.message);
        alert(`Location error: ${err.message}`);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 60000
      }
    );
  };

  return (
    <div className="leaflet-bottom leaflet-right" style={{ pointerEvents: 'none' }}>
      <div className="leaflet-control leaflet-bar">
        <a
          href="#"
          title="My location"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleLocateClick();
          }}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "45px",
            height: "45px",
            backgroundColor: "white",
            borderRadius: "4px",
            boxShadow: "0 1px 5px rgba(0,0,0,0.4)",
            cursor: "pointer",
            pointerEvents: 'auto',
          }}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            width="18"
            height="18"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M12 2a10 10 0 0 0-10 10 10 10 0 0 0 10 10 10 10 0 0 0 10-10A10 10 0 0 0 12 2z" />
            <circle cx="12" cy="12" r="4" />
          </svg>
        </a>
      </div>
    </div>
  );
}

export default function NewLocationPicker({
  openLocationFormDailog,
  latlangVal,
  updateLocationFormData,
  closeMapModal,
  is_from_update,
  latitude,
  longitude
}) {
  const [latLng, setLatLng] = useState(is_from_update && latitude && longitude ? {
    lat: latitude,
    lng: longitude,
  } : null);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(14);
  const [locationDetails, setLocationDetails] = useState(null);
  const [mapType, setMapType] = useState('Hybrid');
  const mapRef = useRef();
  const searchTimeoutRef = useRef();
  const [visibleCount, setVisibleCount] = useState(5);

  // Google Maps tile URLs (no API key required for basic tiles)
  const googleTiles = {
    roadmap: 'https://mt1.google.com/vt/lyrs=m&x={x}&y={y}&z={z}',
    satellite: 'https://mt1.google.com/vt/lyrs=s&x={x}&y={y}&z={z}',
    hybrid: 'https://mt1.google.com/vt/lyrs=y&x={x}&y={y}&z={z}',
    terrain: 'https://mt1.google.com/vt/lyrs=p&x={x}&y={y}&z={z}'
  };

  useEffect(() => {

    if (!(is_from_update && latitude && longitude)) {
      navigator.geolocation.getCurrentPosition(
        async (pos) => {
          const coords = {
            lat: pos.coords.latitude,
            lng: pos.coords.longitude,
          };
          setLatLng(coords);
          setZoomLevel(16);

          const { lat, lng } = coords;

          try {
            const data = await reverseGeocode(lat, lng);
            updateLocationFormData(data.address);
            setLocationDetails({
              displayName: data.display_name,
              address: data.address,
              coordinates: { lat, lng }
            });
          } catch (error) {
            console.warn("Initial geocode error:", error);
          }
        },
        (err) => {
          console.warn("Geolocation error:", err.message);
          setLatLng({ lat: 24.7136, lng: 46.6753 }); // Default to Riyadh coordinates
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 60000
        }
      );

      return () => {
        clearTimeout(searchTimeoutRef.current);
      };
    }

  }, []);

  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);

    if (query.trim().length < 3) {
      setSearchResults([]);
      setShowResults(false);
      return;
    }

    clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      performSearch(query);
    }, 500);
  };

  const performSearch = async (query) => {
    setIsSearching(true);
    setShowResults(true);
    try {
      const results = await forwardGeocode(query, 20);
      setSearchResults(results || []);
      setVisibleCount(5);
    } catch (error) {
      console.error("Search error:", error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const handleResultClick = async (result) => {
    const newLatLng = {
      lat: parseFloat(result.lat),
      lng: parseFloat(result.lon)
    };
    setLatLng(newLatLng);
    setSearchQuery(result.display_name);
    setShowResults(false);
    setZoomLevel(16);

    try {
      const data = await reverseGeocode(newLatLng.lat, newLatLng.lng);
      updateLocationFormData(data.address);
      setLocationDetails({
        displayName: data.display_name,
        address: data.address,
        coordinates: newLatLng
      });
    } catch (error) {
      console.error("Reverse geocoding failed:", error);
    }
  };

  const handleClose = () => {
    closeMapModal();
  };

  const handleOpenLocationFormDailog = () => {
    openLocationFormDailog();
    latlangVal(latLng);
  };

  const changeMapType = (type) => {
    setMapType(type);
  };

  return (
    <Dialog
      fullScreen
      open
      onClose={handleClose}
      sx={{
        "& .MuiDialog-paper": {
          backgroundColor: setMobileBackColor(),
          color: setMobileTextColor(),
        },
      }}
    >
      <DialogTitle sx={{ p: 2, position: "relative" }}>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <IconButton
            edge="start"
            onClick={handleClose}
            aria-label="close"
            sx={{ color: setMobileTextColor() }}
          >
            <CloseIcon />
          </IconButton>
          <Typography variant="h6" component="div" sx={{ flex: 1, ml: 1 }}>
            {t("Pick a Location")}
          </Typography>
        </Box>

        <Box sx={{ position: "relative" }}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder={t("Search for a location...")}
            value={searchQuery}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: isSearching ? (
                <InputAdornment position="end">
                  <CircularProgress size={20} />
                </InputAdornment>
              ) : null,
            }}
            sx={{
              mt: 2,
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                backgroundColor: "background.paper",
              }
            }}
          />

          {showResults && (
            <Paper
              sx={{
                position: "absolute",
                top: "100%",
                left: 0,
                right: 0,
                zIndex: 1300,
                maxHeight: 300,
                overflow: "auto",
                mt: 1,
                boxShadow: 3,
              }}
            >
              <List>
                {searchResults.length > 0 ? (
                  <>
                    {searchResults.slice(0, visibleCount).map((result, index) => (
                      <ListItem
                        button
                        key={index}
                        onClick={() => handleResultClick(result)}
                        sx={{
                          "&:hover": {
                            backgroundColor: "action.hover",
                          }
                        }}
                      >
                        <ListItemText
                          primary={result.display_name}
                          primaryTypographyProps={{ noWrap: true }}
                        />
                      </ListItem>
                    ))}

                    {visibleCount < searchResults.length && (
                      <ListItem
                        button
                        onClick={() => setVisibleCount((prev) => prev + 5)}
                        sx={{ justifyContent: "center" }}
                      >
                        <Typography variant="body2" color="primary">
                          {t("Show more")}
                        </Typography>
                      </ListItem>
                    )}
                  </>
                ) : (
                  <ListItem>
                    <ListItemText primary={t("No results found")} />
                  </ListItem>
                )}
              </List>
            </Paper>
          )}
        </Box>

        {/* Map Type Selector */}
        {/* <Box className="alignByLang" sx={{ mt: 1, display: 'flex', gap: 1, justifyContent: "center" }}>

          <Button
            className="head-font"
            size="small"
            variant={mapType === 'hybrid' ? "contained" : "outlined"}
            onClick={() => changeMapType('hybrid')}
          >
            {t("Hybrid")}
          </Button>
          <Button
            className="head-font"
            size="small"
            variant={mapType === 'satellite' ? "contained" : "outlined"}
            onClick={() => changeMapType('satellite')}
          >
            {t("Satellite")}
          </Button>
          <Button
            className="head-font"
            size="small"
            variant={mapType === 'roadmap' ? "contained" : "outlined"}
            onClick={() => changeMapType('roadmap')}
          >
            {t("Road")}
          </Button>



        </Box> */}
      </DialogTitle>

      <DialogContent sx={{ p: 0, display: "flex", flexDirection: "column" }}>
        <Box sx={{ flex: 1, position: "relative" }}>
          {latLng ? (
            <MapContainer
              center={latLng}
              zoom={zoomLevel}
              style={{ height: "100%", width: "100%" }}
              whenCreated={(map) => { mapRef.current = map; }}
            >
              {/* Google Maps Tile Layer */}
              {/* <TileLayer
                url={googleTiles[mapType]}
                attribution='&copy; <a href="https://www.google.com/maps">Google Maps</a>'
                maxZoom={20}
                minZoom={1}
              /> */}

              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution='&copy; <a href="https://www.openstreetmap.org/">OpenStreetMap</a> contributors'
              />

              <Marker position={latLng} />
              <MapController center={latLng} zoom={zoomLevel} />
              <LocationSelector
                setLatLng={setLatLng}
                updateLocationFormData={updateLocationFormData}
                setLocationDetails={setLocationDetails}
              />

              <LocateControl
                setLatLng={setLatLng}
                updateLocationFormData={updateLocationFormData}
                setLocationDetails={setLocationDetails}
              />
            </MapContainer>
          ) : (
            <Box sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%"
            }}>
              <CircularProgress />
              <Typography sx={{ ml: 2 }}>{t("Loading location...")}</Typography>
            </Box>
          )}
        </Box>

        {/* Location Details Section */}
        {locationDetails && (
          <Box sx={{
            p: 1,
            borderTop: `1px solid ${setMobileCardBorderColor()}`,
            backgroundColor: setMobileBackColor()
          }}>
            <Stack direction="column" >
              <Typography className="sub-font" variant="h6" fontWeight="bold">
                {locationDetails.displayName.split(",")[0]}
              </Typography>

              <Typography className="sub-font" variant="body2">
                {[
                  locationDetails.address.road && `${t("Street")}: ${locationDetails.address.road}`,
                  locationDetails.address.house_number && `${t("House Number")}: ${locationDetails.address.house_number}`,
                  locationDetails.address.postcode && `${t("Postal Code")}: ${locationDetails.address.postcode}`,
                  (locationDetails.address.city || locationDetails.address.town) &&
                  `${t("City")}: ${locationDetails.address.city || locationDetails.address.town}`,
                  locationDetails.address.country && `${t("Country")}: ${locationDetails.address.country}`
                ]
                  .filter(Boolean)
                  .join(', ')}
              </Typography>
            </Stack>
          </Box>
        )}

        <Box sx={{ p: 2, borderTop: `1px solid ${setMobileCardBorderColor()}` }}>
          <Button
            fullWidth
            variant="contained"
            size="large"
            onClick={handleOpenLocationFormDailog}
            disabled={!latLng}
            sx={{
              py: 1.5,
              borderRadius: 2,
              fontWeight: "bold",
              fontSize: "1rem"
            }}
          >
            {t("Confirm Location")}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
}